import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, of, Subscription } from 'rxjs';
import { map, catchError, switchMap, finalize } from 'rxjs/operators';
import { UserModel } from '../models/user.model';
import { AuthModel } from '../models/auth.model';
import { AuthHTTPService } from './auth-http';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';

export type UserType = UserModel | undefined;

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;

  // public fields
  currentUser$: Observable<any>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<any>;
  isLoadingSubject: BehaviorSubject<boolean>;

  get currentUserValue(): any {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: any) {
    this.currentUserSubject.next(user);
  }

  constructor(
    private authHttpService: AuthHTTPService,
    private router: Router
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<any>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
    const subscr = this.getUserByToken().subscribe();
    this.unsubscribe.push(subscr);
  }

  // public methods
  login(email: string, password: string, cftoken?: string, verify?: string): Observable<UserType> {
    this.isLoadingSubject.next(true);
    const emailBase64 = btoa(email);
    const passwordBase64 = btoa(password);

    return this.authHttpService.login(emailBase64, passwordBase64, cftoken, verify).pipe(
      map((auth: any) => {
        return auth;
      }),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  verifyLogin(session: string, code: string, owner_code: string): Observable<UserType> {
    this.isLoadingSubject.next(true);

    return this.authHttpService.verifyLogin(session, code, owner_code).pipe(
      switchMap((auth: any) => {
        console.log("auth after verify: ", auth);

        if (auth?.success) {
          const result = this.setAuthFromLocalStorage(auth);
          // return user observable if success
          return this.getUserByToken();
        } else {
          // return observable of undefined if failed
          return of(undefined);
        }
      }),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  changePassword(currentPassword: string, newPassword: string): Observable<any> {
    this.isLoadingSubject.next(true);
    const auth = this.getAuthFromLocalStorage()?.access_token;
    return this.authHttpService.changePassword(auth,currentPassword,newPassword).pipe(
      map((response: any) => {
        // console.log("response data:",response);
        return response;
      }),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  changePhotoProfile(body: FormData): Observable<any> {
    this.isLoadingSubject.next(true);
    const auth = this.getAuthFromLocalStorage()?.access_token;
    return this.authHttpService.changePhotoProfile(auth,body).pipe(
      map((response: any) => {
        // console.log("response data:",response);
        return response;
      }),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }


  logoutApp(){
    this.isLoadingSubject.next(true);
    const auth = this.getAuthFromLocalStorage().access_token;
    //console.log('logoutApp auth: ',auth);

    this.authHttpService.logout(auth).subscribe({
      next: (res) => {
        this.isLoadingSubject.next(false);
        console.log("response logout: ",res);
      },
      error: (error) => {
        console.error('logout error:', error);
        //return of(undefined);
      },
      complete: () => {
        this.isLoadingSubject.next(false);
        localStorage.removeItem(this.authLocalStorageToken);
        document.location.reload();
      },
    })
  }

  logout() {
    this.authHttpService.logout(this.getAuthFromLocalStorage()?.access_token).subscribe();
    localStorage.removeItem(this.authLocalStorageToken);
    this.router.navigate(['/auth/login'], {
      queryParams: {},
    });
  }

  getUserByToken(): Observable<any> {
    const auth = this.getAuthFromLocalStorage();

    if (!auth || !auth.access_token) {
      this.logout();
      return of(undefined);
    }
    const user = { name: auth.name, email: auth.email, resource_access: auth.resource_access, role: auth.role, owner_code: auth.owner_code, profile_photo_path: auth.profile_photo_path, };
    this.currentUserSubject.next(user);
    return of(user);
  }

  // need create new user then login
  registration(user: UserModel): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.createUser(user).pipe(
      map(() => {
        this.isLoadingSubject.next(false);
      }),
      switchMap(() => this.login(user.email, user.password)),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  forgotPassword(body: FormData): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.forgotPassword(body).pipe(
      map((response: any) => {
        //console.log("response data:",response);
        return response;
      }),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  registerTOTP(): Observable<any> {
    const token = this.getAuthFromLocalStorage().access_token;

    return this.authHttpService.registerTOTP(token).pipe(
      map((response: any) => {
        // console.log("response data totp :",response);
        return response;
      }),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  verifyRegisterTOTP(code: string): Observable<any> {
    const token = this.getAuthFromLocalStorage().access_token;

    return this.authHttpService.verifyRegisterTOTP(token, code).pipe(
      map((response: any) => {
        // console.log("response data totp :",response);
        return response;
      }),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // private methods
  private setAuthFromLocalStorage(auth: any): boolean {
    // store auth authToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
    if (auth.success) {
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth.data));
      return true;
    }
    return false;
  }

  public getAuthFromLocalStorage(): any {
    try {
      const lsValue = localStorage.getItem(this.authLocalStorageToken);
      if (!lsValue) {
        return undefined;
      }

      const authData = JSON.parse(lsValue);
      return authData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
